/* eslint-disable @typescript-eslint/no-explicit-any */
/** Compensation: project tab (senior 3-level group model), allocation editor,
 *  My Earnings, Payments tracker. The project remainder is split across GROUPS
 *  (groupPct, ≤100), each group's pot across its TASKS (groupTaskPct, ≤100), and
 *  each task across PEOPLE (split pct, =100). Ported & extended from compensation.jsx. */
import { useState, useEffect, useRef } from 'react';
import type { HeroData } from '../lib/seed';
import { useData, useDataActions } from '../context/AppDataContext';
import { useCan } from '../context/AuthContext';
import { Ico, Avatar, Modal, CategoryChip, StatusBadge, fmt, useToast } from '../components/ui';
import { apiClient, ApiClientError } from '../lib/apiClient';
import { downloadCsv, dateStamp } from '../lib/exportUtils';
import { effectiveEarning, centralFundAmount, compensationRemainder, allocBase, groupPotAmount } from '@heros/shared';

export function splitState(comp: any, split: any, D: HeroData): string {
  const t = D.timeline.find((x) => x.id === comp.taskId);
  if (split.received) return 'received';
  if (t && t.status === 'completed') return 'awaiting-payment';
  return 'pending-task';
}

export const SPLIT_STATE: Record<string, { label: string; tone: string }> = {
  'pending-task': { label: 'Awaiting task completion', tone: 'slate' },
  'awaiting-payment': { label: 'Awaiting payment', tone: 'amber' },
  received: { label: 'Received', tone: 'green' },
};

export function SplitStateChip({ state }: { state: string }) {
  const m = SPLIT_STATE[state] || SPLIT_STATE['pending-task'];
  return <span className="chip" data-tone={m.tone}><span className="dot" />{m.label}</span>;
}

const byOrder = (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0);

/** The member's %responsibility on the linked timeline task (the WORK split), or null
 *  if they aren't a work assignee. Mirrors lib/workload.pctForUser so "work %" reconciles
 *  with the Team Workload matrix. Compensation % (money) and work % are SEPARATE systems —
 *  showing both lets you see whether someone's pay matches their share of the work. */
const workPctOf = (t: any, uid: string): number | null => {
  if (!t) return null;
  const ws = (t.splits || []).find((s: any) => s.user === uid);
  if (ws) return Math.round(+ws.pct || 0);
  if (t.user === uid && !(t.splits || []).length) return 100;
  return null;
};

export function CompensationTab({ p, updateComp, toggleReceived, currentUser }: any) {
  const D = useData();
  const {
    deleteCompensation, updateProject,
    createCompGroup, updateCompGroup, deleteCompGroup, reorderCompGroups, moveCompToGroup, seedDefaultGroups,
  } = useDataActions();
  const toast = useToast();
  const canManage = useCan('comp.allocate') === 'allow';
  const canReceiveSelf = useCan('comp.received.self') !== 'deny';
  const canReceiveAny = useCan('comp.received.any') !== 'deny';

  const comps = D.compensations.filter((c) => c.projectId === p.id).slice().sort(byOrder);
  const groups = D.compensationGroups.filter((g: any) => g.projectId === p.id).slice().sort(byOrder);
  const [editing, setEditing] = useState<any>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropZone, setDropZone] = useState<string | null>(null); // groupId | 'ungrouped'

  // --- Trial money model: actual receipts → central-fund skim → remainder, which
  //     is split across groups → tasks → people. ---
  const remainder = compensationRemainder(p.actualReceived, p.centralFundPct);
  const cfAmount = centralFundAmount(p.actualReceived, p.centralFundPct);
  const [actualInput, setActualInput] = useState<string>(p.actualReceived != null ? String(p.actualReceived) : '');
  const [cfInput, setCfInput] = useState<string>(p.centralFundPct != null ? String(p.centralFundPct) : '');
  useEffect(() => { setActualInput(p.actualReceived != null ? String(p.actualReceived) : ''); }, [p.actualReceived]);
  useEffect(() => { setCfInput(p.centralFundPct != null ? String(p.centralFundPct) : ''); }, [p.centralFundPct]);
  const commitMoney = async (patch: { actualReceived?: number | null; centralFundPct?: number | null }) => {
    try { await updateProject(p.id, patch as any); }
    catch { toast('Could not save the trial money fields'); }
  };

  // Group budget (groups should sum ≤100).
  const groupPctSum = groups.reduce((s: number, g: any) => s + (g.groupPct || 0), 0);
  const groupRemaining = Math.max(0, 100 - groupPctSum);
  const allocatedToTasks = comps.reduce((s, c) => s + c.base, 0);

  // Split-level rollups (received / awaiting / pending) across ALL comps.
  const lines: any[] = [];
  comps.forEach((c) => c.splits.forEach((sp) => {
    const t = D.timeline.find((x) => x.id === c.taskId);
    lines.push({ comp: c, split: sp, taskDone: t && t.status === 'completed', share: effectiveEarning(c.base, sp) });
  }));
  const received = lines.filter((l) => l.split.received).reduce((s, l) => s + l.share, 0);
  const awaiting = lines.filter((l) => !l.split.received && l.taskDone).reduce((s, l) => s + l.share, 0);
  const pendingTask = lines.filter((l) => !l.split.received && !l.taskDone).reduce((s, l) => s + l.share, 0);

  const perPerson: Record<string, any> = {};
  lines.forEach((l) => {
    const uid = l.split.user;
    if (!perPerson[uid]) perPerson[uid] = { total: 0, received: 0, awaiting: 0, pending: 0 };
    perPerson[uid].total += l.share;
    if (l.split.received) perPerson[uid].received += l.share;
    else if (l.taskDone) perPerson[uid].awaiting += l.share;
    else perPerson[uid].pending += l.share;
  });

  const membersOf = (gid: string) => comps.filter((c) => c.groupId === gid);
  const ungrouped = comps.filter((c) => !c.groupId);

  // ---- group / task mutations ----
  const addGroup = async () => {
    try { await createCompGroup(p.id, `Phase ${groups.length + 1}`, 0); }
    catch { /* toasted */ }
  };
  const onSeedDefaults = async () => {
    if (comps.length && !window.confirm('Set up the default phases (Phase 1/2/3 + Other at 10/40/50) and bucket the tasks by name? Existing phase membership is kept.')) return;
    try { await seedDefaultGroups(p.id); toast('Default phases set up'); } catch { /* toasted */ }
  };
  const commitGroupName = async (g: any, name: string) => {
    const v = name.trim();
    if (!v || v === g.name) return;
    try { await updateCompGroup(g.id, { name: v }); } catch { /* toasted */ }
  };
  const commitGroupPct = async (g: any, raw: string) => {
    const max = groupRemaining + (g.groupPct || 0); // budget available to THIS group
    const v = Math.max(0, Math.min(max, +raw || 0));
    if (v === (g.groupPct || 0)) return;
    try { await updateCompGroup(g.id, { groupPct: v }); } catch { /* toasted */ }
  };
  const moveGroup = (i: number, dir: number) => {
    const j = i + dir;
    if (j < 0 || j >= groups.length) return;
    const ids = groups.map((g: any) => g.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    void reorderCompGroups(p.id, ids);
  };
  const onDeleteGroup = async (g: any) => {
    if (membersOf(g.id).length) { toast('Move this phase’s tasks out before deleting it'); return; }
    if (!window.confirm(`Delete the empty phase "${g.name}"?`)) return;
    try { await deleteCompGroup(g.id); toast('Phase deleted'); } catch { /* toasted */ }
  };
  const commitTaskPct = async (c: any, raw: string) => {
    if (!c.groupId) return;
    const others = membersOf(c.groupId).filter((x) => x.id !== c.id && x.groupTaskPct != null)
      .reduce((s, x) => s + (x.groupTaskPct || 0), 0);
    const max = Math.max(0, 100 - others);
    const v = Math.max(0, Math.min(max, +raw || 0));
    if (v === (c.groupTaskPct ?? 0)) return;
    try { await moveCompToGroup(c.id, c.groupId, v); } catch { /* toasted */ }
  };
  const onDeleteRow = async (c: any) => {
    const t = D.timeline.find((x) => x.id === c.taskId);
    if (!window.confirm(`Delete the compensation row "${c.label || t?.name || 'task'}"? This removes its split allocation.`)) return;
    try { await deleteCompensation(c.id); toast('Compensation row deleted'); } catch { /* toasted */ }
  };
  const dropTo = async (zone: string | null) => {
    const id = dragId; setDragId(null); setDropZone(null);
    if (!id) return;
    const c = comps.find((x) => x.id === id);
    if (!c) return;
    const targetGid = zone === 'ungrouped' ? null : zone;
    if ((c.groupId ?? null) === (targetGid ?? null)) return; // no-op
    try { await moveCompToGroup(id, targetGid); }
    catch { /* toasted */ }
  };

  // ---- shared renderers ----
  const renderSplits = (c: any) => {
    const t = D.timeline.find((x) => x.id === c.taskId);
    const taskDone = t && t.status === 'completed';
    const sumPct = c.splits.reduce((s: number, sp: any) => s + sp.pct, 0);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {c.splits.map((sp: any, i: number) => {
          const u = D.team.find((x) => x.id === sp.user);
          const st = splitState(c, sp, D);
          const isOwn = currentUser && sp.user === currentUser.id;
          const allowed = isOwn ? canReceiveSelf : canReceiveAny;
          const wp = workPctOf(t, sp.user);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, padding: '4px 6px', borderRadius: 4, background: sp.received ? 'rgba(46,106,60,0.06)' : 'var(--surface-alt)' }}>
              <Avatar user={u} size="sm" />
              <span style={{ flex: 1, minWidth: 64 }}>{u?.name}</span>
              <span className="mono tabular" title="Share of this task's MONEY" style={{ width: 34, textAlign: 'right', color: 'var(--text-2)' }}>{sp.pct}%</span>
              <span className="mono tabular" title="Share of the WORK on this task (from the Timeline %responsible split)" style={{ width: 56, textAlign: 'right', fontSize: 10, color: wp != null && wp !== sp.pct ? 'var(--t-amber-fg)' : 'var(--text-3)' }}>{wp != null ? `${wp}% wk` : '— wk'}</span>
              <span className="mono tabular" title="This person's earning = task ฿ × their money %" style={{ width: 70, textAlign: 'right', fontWeight: 600 }}>{fmt.thb(effectiveEarning(c.base, sp))}</span>
              <SplitStateChip state={st} />
              <button
                className={'btn xs ' + (sp.received ? '' : 'primary')}
                onClick={async () => { try { await toggleReceived(c.id, sp.user); } catch { toast('Could not update this line'); } }}
                disabled={(!taskDone && !sp.received) || !allowed}
                title={!allowed ? 'No permission to update this line' : !taskDone && !sp.received ? 'Task must be completed first' : sp.received ? `Received ${sp.receivedOn}` : 'Tick once payment is received'}
                style={{ minWidth: 90 }}
              >
                {sp.received ? <><Ico.check /> {sp.receivedOn}</> : 'Mark received'}
              </button>
            </div>
          );
        })}
        {sumPct !== 100 && <div style={{ fontSize: 10.5, color: 'var(--accent)' }}>⚠ Splits sum to {sumPct}% (must be 100%)</div>}
      </div>
    );
  };

  // One task row, shown inside a group (showPct = within-group %) or ungrouped (legacy % of remainder).
  const renderTaskRow = (c: any, mode: 'group' | 'ungrouped', pot = 0) => {
    const t = D.timeline.find((x) => x.id === c.taskId);
    // Realized % of the phase pot, derived from the row's ฿ — links the % to the money even
    // for PINNED (manual-฿) rows whose editable groupTaskPct is blank. Updates live as the
    // pot rescales (e.g. when Central fund % changes), so % and ฿ always stay consistent.
    const realizedPct = pot > 0 ? Math.round((c.base / pot) * 100) : null;
    return (
      <div key={c.id} draggable={canManage} onDragStart={() => setDragId(c.id)} onDragEnd={() => { setDragId(null); setDropZone(null); }}
        style={{ display: 'grid', gridTemplateColumns: '20px 64px 1.4fr 1fr 1.6fr 70px', gap: 8, alignItems: 'start', padding: '8px 6px', borderTop: '1px solid var(--border)', opacity: dragId === c.id ? 0.4 : 1 }}>
        <span title="Drag to another group" style={{ cursor: canManage ? 'grab' : 'default', color: 'var(--text-3)', userSelect: 'none', paddingTop: 4 }}>⠿</span>
        <div className="num">
          {mode === 'group' ? (
            <>
              <input key={'gp' + c.id + (c.groupTaskPct ?? '')} type="number" min="0" max="100" className="tabular mono" disabled={!canManage}
                defaultValue={c.groupTaskPct ?? ''} placeholder="—" title={c.groupTaskPct != null ? "% of this phase's pot (drives the ฿)" : 'Pinned ฿ — type a % here to make it scale with the pot'}
                style={{ width: 56, textAlign: 'right', fontSize: 12 }} onClick={(e) => e.stopPropagation()}
                onBlur={(e) => void commitTaskPct(c, e.target.value)} />
              {realizedPct != null && (
                <div style={{ fontSize: 9.5, color: c.groupTaskPct == null ? 'var(--t-amber-fg)' : 'var(--text-3)', marginTop: 2 }}
                  title={c.groupTaskPct == null ? 'Pinned ฿ as a share of the phase pot (not %-driven — won’t rescale)' : 'Realized share of the phase pot'}>
                  = {realizedPct}%
                </div>
              )}
            </>
          ) : (
            <span style={{ fontSize: 10.5, color: 'var(--text-3)' }} title="No phase: manual base or legacy % of remainder">{c.allocPct != null ? `${c.allocPct}% rem` : 'manual'}</span>
          )}
        </div>
        <div>
          <div style={{ fontWeight: 500, fontSize: 12.5 }}>{c.label || t?.name}</div>
          {t && <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 1 }}><CategoryChip category={t.category} /></div>}
        </div>
        <div style={{ paddingTop: 2 }}>
          {t ? <StatusBadge status={t.status} /> : <span className="chip"><span className="dot" />—</span>}
          <div className="num tabular mono" style={{ fontWeight: 600, fontSize: 12.5, marginTop: 4 }}>{fmt.thb(c.base)}</div>
        </div>
        <div>{renderSplits(c)}</div>
        <div style={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <button className="btn ghost icon sm" onClick={() => setEditing({ ...c, splits: c.splits.map((s: any) => ({ ...s })) })} title="Edit allocation"><Ico.edit /></button>
          {canManage && <button className="btn ghost icon sm danger" title="Delete this row" onClick={() => void onDeleteRow(c)}><Ico.trash /></button>}
        </div>
      </div>
    );
  };

  const dropProps = (zone: string) => ({
    onDragOver: (e: any) => { if (dragId) { e.preventDefault(); if (dropZone !== zone) setDropZone(zone); } },
    onDrop: (e: any) => { e.preventDefault(); void dropTo(zone); },
    style: { outline: dropZone === zone && dragId ? '2px dashed var(--accent)' : undefined, outlineOffset: -2, borderRadius: 8 },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <div className="stat"><div className="label">Allocated to tasks</div><div className="v" style={{ color: allocatedToTasks > remainder ? 'var(--danger, #b3261e)' : undefined }}>{fmt.thb(allocatedToTasks)}</div><div className="d">{comps.length} tasks · {groups.length} phases</div></div>
        <div className="stat"><div className="label">Received</div><div className="v" style={{ color: '#2E6A3C' }}>{fmt.thb(received)}</div><div className="d">{lines.filter((l) => l.split.received).length} of {lines.length} splits</div></div>
        <div className="stat"><div className="label">Awaiting payment</div><div className="v" style={{ color: '#8A5A14' }}>{fmt.thb(awaiting)}</div><div className="d">task done · not yet ticked</div></div>
        <div className="stat"><div className="label">Pending task done</div><div className="v" style={{ color: 'var(--text-2)' }}>{fmt.thb(pendingTask)}</div><div className="d">task still in progress</div></div>
      </div>

      {/* Trial money model: actual received → central-fund skim → remainder → groups → tasks → people. */}
      <div className="card"><div className="card-b" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 150 }}>
          <div className="label">Actual received from trial</div>
          <input type="number" min="0" className="tabular mono" disabled={!canManage} value={actualInput}
            onChange={(e) => setActualInput(e.target.value)}
            onBlur={() => commitMoney({ actualReceived: actualInput.trim() === '' ? null : Math.max(0, Math.round(+actualInput || 0)) })}
            placeholder="manual ฿" style={{ width: 150, fontSize: 15, fontWeight: 700 }} title="Total THB actually received — may differ from the quotation" />
          <div className="sub">quotation ref: {p.activeQuotationTotal ? fmt.thb(p.activeQuotationTotal) : '—'}</div>
        </div>
        <div style={{ minWidth: 120 }}>
          <div className="label">Central fund %</div>
          <input type="number" min="0" max="100" className="tabular mono" disabled={!canManage} value={cfInput}
            onChange={(e) => setCfInput(e.target.value)}
            onBlur={() => commitMoney({ centralFundPct: cfInput.trim() === '' ? null : Math.max(0, Math.min(100, +cfInput || 0)) })}
            placeholder="%" style={{ width: 90, fontSize: 15, fontWeight: 700 }} title="Percent skimmed into the central/common fund before allocation" />
          <div className="sub">= {fmt.thb(cfAmount)} to central fund</div>
        </div>
        <div><div className="label">Remaining to allocate</div><div className="mono tabular" style={{ fontSize: 17, fontWeight: 700, color: 'var(--accent)' }}>{fmt.thb(remainder)}</div><div className="sub">actual − central fund = 100%</div></div>
        <div><div className="label">Σ phase %</div><div className="mono tabular" style={{ fontSize: 17, fontWeight: 700, color: groupPctSum > 100 ? 'var(--danger, #b3261e)' : 'var(--text)' }}>{groupPctSum.toFixed(groupPctSum % 1 ? 1 : 0)}%</div><div className="sub">{groupRemaining > 0 ? `${groupRemaining.toFixed(groupRemaining % 1 ? 1 : 0)}% unallocated` : groupPctSum > 100 ? '⚠ over 100%' : 'fully allocated'}</div></div>
        <div><div className="label">Unallocated remainder</div><div className="mono tabular" style={{ fontSize: 17, fontWeight: 700, color: remainder - allocatedToTasks < 0 ? 'var(--danger, #b3261e)' : 'var(--text)' }}>{fmt.thb(remainder - allocatedToTasks)}</div><div className="sub">remainder − allocated</div></div>
      </div></div>

      <div className="card">
        <div className="card-h">
          <div>
            <h3>Compensation phases</h3>
            <div className="sub">Split the remainder across phases (≤100%), then each phase's pot across its tasks (≤100%). Drag a task by its ⠿ handle to move it between phases.</div>
          </div>
          {canManage && (
            <div style={{ display: 'flex', gap: 6 }}>
              {groups.length === 0 && <button className="btn sm" onClick={() => void onSeedDefaults()} title="Create Phase 1/2/3 (10/40/50) + Other and bucket tasks by name"><Ico.plus /> Set up default phases</button>}
              <button className="btn sm" onClick={() => void addGroup()}><Ico.plus /> Add phase</button>
              <button className="btn primary sm" onClick={() => setEditing({ isNew: true, id: 'c' + Math.random().toString(36).slice(2, 6), projectId: p.id, taskId: D.timeline.filter((t) => t.project === p.id)[0]?.id, label: '', base: 0, groupId: groups[0]?.id ?? null, groupTaskPct: null, splits: [{ user: D.team[0].id, pct: 100, received: false, receivedOn: '' }] })}>
                <Ico.plus /> Add allocation
              </button>
            </div>
          )}
        </div>
        <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {groups.length === 0 && ungrouped.length === 0 && (
            <div style={{ color: 'var(--text-2)', textAlign: 'center', padding: 24 }}>No compensation phases yet. Use “Set up default phases” to start.</div>
          )}

          {groups.map((g: any, gi: number) => {
            const members = membersOf(g.id);
            const pot = groupPotAmount(remainder, g.groupPct);
            const taskPctSum = members.filter((m) => m.groupTaskPct != null).reduce((s, m) => s + (m.groupTaskPct || 0), 0);
            const groupBase = members.reduce((s, m) => s + m.base, 0);
            return (
              <div key={g.id} {...dropProps(g.id)} style={{ ...dropProps(g.id).style, border: '1px solid var(--border)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface-alt)', borderRadius: '8px 8px 0 0', flexWrap: 'wrap' }}>
                  {canManage ? (
                    <input defaultValue={g.name} key={'gn' + g.id + g.name} onBlur={(e) => void commitGroupName(g, e.target.value)} style={{ fontWeight: 700, fontSize: 13.5, width: 160 }} />
                  ) : <div style={{ fontWeight: 700, fontSize: 13.5 }}>{g.name}</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input key={'gpct' + g.id + (g.groupPct ?? '')} type="number" min="0" max="100" className="tabular mono" disabled={!canManage}
                      defaultValue={g.groupPct ?? 0} title={`% of remainder (max ${(groupRemaining + (g.groupPct || 0)).toFixed(0)}%)`}
                      style={{ width: 64, textAlign: 'right', fontSize: 13, fontWeight: 700 }} onBlur={(e) => void commitGroupPct(g, e.target.value)} />
                    <span style={{ color: 'var(--text-2)', fontSize: 12 }}>% → {fmt.thb(pot)}</span>
                  </div>
                  <span className="chip" data-tone={taskPctSum > 100 ? 'amber' : 'slate'} style={{ marginLeft: 'auto' }}>
                    <span className="dot" />tasks {taskPctSum.toFixed(taskPctSum % 1 ? 1 : 0)}%{taskPctSum > 100 ? ' ⚠' : taskPctSum < 100 ? ` · ${(100 - taskPctSum).toFixed(0)}% free` : ''}
                  </span>
                  <span className="mono tabular" style={{ fontSize: 12, color: groupBase > pot ? 'var(--danger, #b3261e)' : 'var(--text-2)' }} title="Sum of this group's task bases vs its pot">{fmt.thb(groupBase)}</span>
                  {canManage && (
                    <div style={{ display: 'flex', gap: 1 }}>
                      <button className="btn ghost icon sm" title="Move phase up" disabled={gi === 0} onClick={() => moveGroup(gi, -1)}>▲</button>
                      <button className="btn ghost icon sm" title="Move phase down" disabled={gi === groups.length - 1} onClick={() => moveGroup(gi, 1)}>▼</button>
                      <button className="btn ghost icon sm danger" title={members.length ? 'Move tasks out before deleting' : 'Delete empty phase'} onClick={() => void onDeleteGroup(g)}><Ico.trash /></button>
                    </div>
                  )}
                </div>
                {members.length === 0
                  ? <div style={{ padding: '12px 10px', fontSize: 11.5, color: 'var(--text-3)', textAlign: 'center' }}>Empty — drag a task here.</div>
                  : members.map((c) => renderTaskRow(c, 'group', pot))}
              </div>
            );
          })}

          {/* Ungrouped (legacy % of remainder or manual). Also a drop target to un-group. */}
          {(ungrouped.length > 0 || (groups.length > 0 && dragId)) && (
            <div {...dropProps('ungrouped')} style={{ ...dropProps('ungrouped').style, border: '1px dashed var(--border)', borderRadius: 8 }}>
              <div style={{ padding: '8px 10px', background: 'var(--surface-alt)', borderRadius: '8px 8px 0 0', fontWeight: 600, fontSize: 12.5, color: 'var(--text-2)' }}>
Tasks with no phase <span className="sub" style={{ fontWeight: 400 }}>— legacy % of remainder or pinned manual amounts. Drag into a phase to include them in the phase budget.</span>
              </div>
              {ungrouped.length === 0
                ? <div style={{ padding: '12px 10px', fontSize: 11.5, color: 'var(--text-3)', textAlign: 'center' }}>Drop here to remove a task from its group.</div>
                : ungrouped.map((c) => renderTaskRow(c, 'ungrouped'))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-h"><h3>Per-person — this project</h3></div>
        <div className="card-b" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
          {p.members.map((uid: string) => {
            const u = D.team.find((x) => x.id === uid);
            const pp = perPerson[uid] || { total: 0, received: 0, awaiting: 0, pending: 0 };
            return (
              <div key={uid} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Avatar user={u} size="sm" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500 }}>{u?.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{u?.role}</div>
                  </div>
                  <div className="mono tabular" style={{ fontWeight: 700, fontSize: 13 }}>{fmt.thb(pp.total)}</div>
                </div>
                <div className="bar-track" style={{ height: 6 }}>
                  <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{ width: (pp.total ? pp.received / pp.total * 100 : 0) + '%', background: '#4a8a55' }} />
                    <div style={{ width: (pp.total ? pp.awaiting / pp.total * 100 : 0) + '%', background: '#C58A24' }} />
                    <div style={{ width: (pp.total ? pp.pending / pp.total * 100 : 0) + '%', background: 'var(--text-3)' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 10.5, color: 'var(--text-2)' }}>
                  <span>Received {fmt.thb(pp.received)}</span>
                  {pp.awaiting > 0 && <span style={{ color: 'var(--t-amber-fg)' }}>Awaiting {fmt.thb(pp.awaiting)}</span>}
                  <span>Pending {fmt.thb(pp.pending)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <CompEditor
          comp={editing}
          project={p}
          groups={groups}
          onClose={() => setEditing(null)}
          onSave={async (c: any) => {
            try { await updateComp(c); setEditing(null); toast('Allocation saved'); }
            catch (e: any) { toast(e?.message || 'Could not save allocation'); }
          }}
        />
      )}
    </div>
  );
}

function CompEditor({ comp, project, groups, onClose, onSave }: any) {
  const D = useData();
  const canOverride = useCan('comp.allocate') === 'allow';
  const [form, setForm] = useState<any>({ ...comp });
  const remainder = compensationRemainder(project.actualReceived, project.centralFundPct);
  const group = groups.find((g: any) => g.id === form.groupId);
  const grouped = !!form.groupId;
  const pot = group ? groupPotAmount(remainder, group.groupPct) : 0;
  // Remaining within-group % budget (mirrors the server clamp) so the preview base
  // matches what will actually be stored.
  const withinRemaining = grouped
    ? Math.max(0, 100 - D.compensations
        .filter((c: any) => c.groupId === form.groupId && c.id !== form.id && c.groupTaskPct != null)
        .reduce((s: number, c: any) => s + (c.groupTaskPct || 0), 0))
    : 100;
  const pctDriven = grouped
    ? (form.groupTaskPct !== undefined && form.groupTaskPct !== null && form.groupTaskPct !== '')
    : (form.allocPct !== undefined && form.allocPct !== null && form.allocPct !== '');
  // Raw-money OR % entry (senior request): the user picks. '%'=derive base from a %,
  // 'baht'=type a raw THB amount (pinned manual base; %s cleared, recompute won't touch it).
  const [amountMode, setAmountMode] = useState<'pct' | 'baht'>(pctDriven ? 'pct' : (Number(form.base) > 0 ? 'baht' : 'pct'));
  // Remember the typed % so toggling ฿ then back to % is non-destructive.
  const lastPct = useRef<{ g: any; a: any }>({ g: null, a: null });
  const toBahtMode = () => {
    lastPct.current = { g: form.groupTaskPct, a: form.allocPct };
    setForm((f: any) => ({ ...f, groupTaskPct: null, allocPct: null }));
    setAmountMode('baht');
  };
  const toPctMode = () => {
    setForm((f: any) => ({ ...f, groupTaskPct: lastPct.current.g, allocPct: lastPct.current.a }));
    setAmountMode('pct');
  };
  const sumPct = form.splits.reduce((s: number, sp: any) => s + (+sp.pct || 0), 0);
  const hasOverride = (s: any) => s.overrideAmount !== undefined && s.overrideAmount !== null && s.overrideAmount !== '';
  const overrideMissingReason = form.splits.some((s: any) => hasOverride(s) && !(s.overrideReason && String(s.overrideReason).trim()));
  const valid = sumPct === 100 && (pctDriven || form.base > 0) && form.splits.every((s: any) => s.user && s.pct) && !overrideMissingReason;

  // Live base preview (server is authoritative on save).
  const previewBase = grouped
    ? Math.round(pot * Math.max(0, +form.groupTaskPct || 0) / 100)
    : (form.allocPct !== undefined && form.allocPct !== null && form.allocPct !== '' ? allocBase(remainder, +form.allocPct) : form.base);

  const upd = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const updGroup = (gid: string) => setForm((f: any) => ({ ...f, groupId: gid || null, ...(gid ? { allocPct: null } : { groupTaskPct: null }) }));
  const updSplit = (i: number, k: string, v: any) => setForm((f: any) => ({ ...f, splits: f.splits.map((s: any, idx: number) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addSplit = () => setForm((f: any) => ({ ...f, splits: [...f.splits, { user: D.team[0].id, pct: 0, received: false, receivedOn: '' }] }));
  const rmSplit = (i: number) => setForm((f: any) => ({ ...f, splits: f.splits.filter((_: any, idx: number) => idx !== i) }));
  const distribute = () => {
    const n = form.splits.length;
    const each = Math.floor(100 / n);
    const rem = 100 - each * n;
    setForm((f: any) => ({ ...f, splits: f.splits.map((s: any, i: number) => ({ ...s, pct: each + (i === 0 ? rem : 0) })) }));
  };

  const projectTasks = D.timeline.filter((t) => t.project === project.id);
  const pullFromTask = () => {
    const t: any = D.timeline.find((x) => x.id === form.taskId);
    const ts = (t?.splits && t.splits.length) ? t.splits : (t?.user ? [{ user: t.user, pct: 100 }] : null);
    if (!ts || !ts.length) return;
    setForm((f: any) => ({ ...f, splits: ts.map((s: any) => ({ user: s.user, pct: s.pct, received: false, receivedOn: '' })) }));
  };

  return (
    <Modal title={comp.isNew ? 'New compensation allocation' : 'Edit allocation'} onClose={onClose} wide
      footer={(
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!valid} onClick={() => onSave({ ...form, base: pctDriven ? previewBase : form.base })}>
            <Ico.check /> Save allocation
          </button>
        </>
      )}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field">
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Linked task</span>
            <button type="button" className="btn ghost xs" onClick={pullFromTask} title="Copy this task's %responsible split (from Timeline) into the allocation below">↻ Use task’s % split</button>
          </label>
          <select value={form.taskId || ''} onChange={(e) => upd('taskId', e.target.value)}>
            {projectTasks.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.category}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Phase</label>
          <select value={form.groupId || ''} onChange={(e) => updGroup(e.target.value)}>
            <option value="">No phase (legacy % of remainder / manual)</option>
            {groups.map((g: any) => <option key={g.id} value={g.id}>{g.name} ({g.groupPct}%)</option>)}
          </select>
        </div>
        <div className="field">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Allocation amount</span>
            <span style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <button type="button" onClick={toPctMode} style={{ border: 'none', cursor: 'pointer', padding: '1px 8px', fontSize: 11, fontWeight: 700, background: amountMode === 'pct' ? 'var(--accent)' : 'var(--surface)', color: amountMode === 'pct' ? '#fff' : 'var(--text-2)' }}>%</button>
              <button type="button" onClick={toBahtMode} style={{ border: 'none', cursor: 'pointer', padding: '1px 8px', fontSize: 11, fontWeight: 700, background: amountMode === 'baht' ? 'var(--accent)' : 'var(--surface)', color: amountMode === 'baht' ? '#fff' : 'var(--text-2)' }}>฿</button>
            </span>
          </label>
          {amountMode === 'pct' ? (
            grouped ? (
              <>
                <input type="number" min="0" max={withinRemaining} className="tabular mono" value={form.groupTaskPct ?? ''} onChange={(e) => upd('groupTaskPct', e.target.value === '' ? null : Math.max(0, Math.min(withinRemaining, +e.target.value || 0)))} placeholder="% of this phase's pot" />
                <div className="sub">pot {fmt.thb(pot)} · {withinRemaining}% free · {pctDriven ? `= ${fmt.thb(previewBase)}` : 'enter a %'}</div>
              </>
            ) : (
              <>
                <input type="number" min="0" max="100" className="tabular mono" value={form.allocPct ?? ''} onChange={(e) => upd('allocPct', e.target.value === '' ? null : Math.max(0, Math.min(100, +e.target.value || 0)))} placeholder="% of remaining trial money" title={`Remaining trial money: ${fmt.thb(remainder)}`} />
                <div className="sub">{form.allocPct != null && form.allocPct !== '' ? `= ${fmt.thb(allocBase(remainder, +form.allocPct))} of ${fmt.thb(remainder)} remainder` : 'enter a % of the remainder'}</div>
              </>
            )
          ) : (
            <>
              <input type="number" min="0" className="tabular mono" value={form.base || ''} placeholder="raw ฿ amount" onChange={(e) => setForm((f: any) => ({ ...f, base: Math.max(0, Math.round(+e.target.value || 0)), groupTaskPct: null, allocPct: null }))} />
              <div className="sub">
                {grouped && pot > 0 ? `≈ ${(form.base / pot * 100).toFixed(1)}% of the ${fmt.thb(pot)} pot · ` : (!grouped && remainder > 0 ? `≈ ${(form.base / remainder * 100).toFixed(1)}% of the ${fmt.thb(remainder)} remainder · ` : '')}
                pinned manual amount (won’t auto-recompute)
              </div>
            </>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Note (optional)</label>
          <input value={form.note || ''} onChange={(e) => upd('note', e.target.value)} placeholder="e.g. milestone payment on DB lock" />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Split allocation</h4>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn ghost sm" onClick={distribute}>Split evenly</button>
            <button className="btn ghost sm" onClick={addSplit}><Ico.plus /> Person</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {form.splits.map((s: any, i: number) => {
            const u = D.team.find((x) => x.id === s.user);
            const base = pctDriven ? previewBase : form.base;
            const share = effectiveEarning(base, { pct: +s.pct || 0, overrideAmount: hasOverride(s) ? +s.overrideAmount : undefined });
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 64px 22px 100px 92px 26px', gap: 6, alignItems: 'center' }}>
                  <Avatar user={u} size="sm" />
                  <select value={s.user} onChange={(e) => updSplit(i, 'user', e.target.value)}>
                    {D.team.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
                  </select>
                  <input className="tabular mono" type="number" min="0" max="100" value={s.pct} style={{ textAlign: 'right' }} onChange={(e) => updSplit(i, 'pct', +e.target.value || 0)} />
                  <span style={{ color: 'var(--text-2)' }}>%</span>
                  <span className="mono tabular" style={{ textAlign: 'right', fontWeight: 600, color: hasOverride(s) ? 'var(--accent)' : 'var(--text)' }}>{fmt.thb(share)}</span>
                  <input className="tabular mono" type="number" min="0" placeholder="override ฿" title="Authorized manual earning override — leave blank to use % of total" disabled={!canOverride} value={s.overrideAmount ?? ''} style={{ textAlign: 'right', fontSize: 11 }} onChange={(e) => updSplit(i, 'overrideAmount', e.target.value === '' ? undefined : Math.max(0, +e.target.value || 0))} />
                  <button className="btn ghost icon sm" onClick={() => rmSplit(i)} disabled={form.splits.length <= 1}><Ico.close /></button>
                </div>
                {hasOverride(s) && (
                  <input value={s.overrideReason || ''} onChange={(e) => updSplit(i, 'overrideReason', e.target.value)} placeholder="Reason for override (required)" style={{ fontSize: 11, marginLeft: 34, borderColor: s.overrideReason ? 'var(--border)' : 'var(--accent)' }} />
                )}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 12, padding: 10, background: sumPct === 100 ? 'var(--surface-alt)' : 'var(--accent-soft)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: sumPct === 100 ? 'var(--text-2)' : 'var(--accent)' }}>
            {sumPct === 100 ? '✓ Splits total 100%' : `⚠ Splits total ${sumPct}% — must equal 100%`}
          </span>
          <span className="mono tabular" style={{ fontSize: 13, fontWeight: 700 }}>{fmt.thb(pctDriven ? previewBase : form.base)}</span>
        </div>
        <div style={{ marginTop: 10, padding: 8, background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>
          ℹ Payments are made directly to each person. The recipient ticks "received" on their own line in their <strong>My Earnings</strong> page once payment has cleared.
        </div>
      </div>
    </Modal>
  );
}

export function MyEarningsPage({ currentUser, toggleReceived }: any) {
  const D = useData();
  const toast = useToast();
  const canReceiveSelf = useCan('comp.received.self') !== 'deny';

  const allLines: any[] = [];
  D.compensations.forEach((c) => {
    c.splits.forEach((sp, idx) => {
      if (sp.user === currentUser.id) {
        const t = D.timeline.find((x) => x.id === c.taskId);
        const p = D.projects.find((x) => x.id === c.projectId);
        const state = splitState(c, sp, D);
        allLines.push({ comp: c, split: sp, task: t, project: p, share: effectiveEarning(c.base, sp), state, idx });
      }
    });
  });

  // Year filter: date a line by when it was received, else the task's end/start date.
  // Undated lines have a blank year and only show under "All time".
  const lineDate = (l: any) => l.split.receivedOn || l.task?.endDate || l.task?.startDate || '';
  const lineYear = (l: any) => lineDate(l).slice(0, 4);
  const years = Array.from(new Set(allLines.map(lineYear).filter(Boolean))).sort().reverse();
  const currentYear = String(new Date().getFullYear());
  const [yearFilter, setYearFilter] = useState<string>(years.includes(currentYear) ? currentYear : 'all');

  const lines = yearFilter === 'all' ? allLines : allLines.filter((l) => lineYear(l) === yearFilter);

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportStatement = async () => {
    setPdfBusy(true);
    try {
      const blob = await apiClient.compensation.statementPdf({ year: yearFilter === 'all' ? 'all' : Number(yearFilter) });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `earnings-statement-${dateStamp()}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      const m = err instanceof ApiClientError ? (err.body?.message ?? '') : String((err as any)?.message || '');
      const unavailable = (err instanceof ApiClientError && err.status === 503) || /not installed|unavailable|503|latex|pdflatex/i.test(m);
      toast(unavailable ? 'Server LaTeX is unavailable — PDF export needs pdflatex installed on the server.' : 'Statement PDF failed — please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const total = lines.reduce((s, l) => s + l.share, 0);
  const received = lines.filter((l) => l.state === 'received').reduce((s, l) => s + l.share, 0);
  const awaiting = lines.filter((l) => l.state === 'awaiting-payment').reduce((s, l) => s + l.share, 0);
  const pending = lines.filter((l) => l.state === 'pending-task').reduce((s, l) => s + l.share, 0);

  const byProject: Record<string, any> = {};
  lines.forEach((l) => {
    if (!byProject[l.project.id]) byProject[l.project.id] = { project: l.project, total: 0, received: 0, awaiting: 0, pending: 0, lines: [] };
    const bp = byProject[l.project.id];
    bp.total += l.share;
    if (l.state === 'received') bp.received += l.share;
    else if (l.state === 'awaiting-payment') bp.awaiting += l.share;
    else bp.pending += l.share;
    bp.lines.push(l);
  });

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>My Earnings</h2>
          <div className="sub">Tick "received" on each line once payment from the project has cleared into your account — {currentUser.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={{ width: 'auto' }} value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
            <option value="all">All time</option>
          </select>
          <button className="btn" disabled={pdfBusy} onClick={() => void exportStatement()}><Ico.dl /> {pdfBusy ? 'Generating…' : 'Statement PDF'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        <div className="stat" style={{ background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' }}>
          <div className="label" style={{ color: 'rgba(255,255,255,0.7)' }}>Total income {yearFilter === 'all' ? '(all time)' : yearFilter}</div>
          <div className="v" style={{ fontSize: 26 }}>{fmt.thb(total)}</div>
          <div className="d" style={{ color: 'rgba(255,255,255,0.7)' }}>{lines.length} compensation lines</div>
        </div>
        <div className="stat"><div className="label">Received</div><div className="v" style={{ color: '#2E6A3C' }}>{fmt.thb(received)}</div><div className="d">{lines.filter((l) => l.state === 'received').length} lines ticked</div></div>
        <div className="stat"><div className="label">Awaiting payment</div><div className="v" style={{ color: '#8A5A14' }}>{fmt.thb(awaiting)}</div><div className="d">task done · not yet received</div></div>
        <div className="stat"><div className="label">Pending task done</div><div className="v" style={{ color: 'var(--text-2)' }}>{fmt.thb(pending)}</div><div className="d">tasks still in progress</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <div className="card">
          <div className="card-h">
            <h3>Income by project</h3>
            <span className="chip" data-tone="slate"><span className="dot" />{Object.keys(byProject).length} projects</span>
          </div>
          <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.values(byProject).map((bp: any) => (
              <div key={bp.project.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      <span className="mono" style={{ color: 'var(--accent)' }}>{bp.project.code}</span>
                      <span style={{ marginLeft: 8, color: 'var(--text)' }}>{bp.project.shortTitle}</span>
                    </div>
                    <div className="sub" style={{ fontSize: 11.5, marginTop: 1 }}>{bp.project.title.length > 80 ? bp.project.title.slice(0, 80) + '…' : bp.project.title}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="mono tabular" style={{ fontWeight: 700, fontSize: 16 }}>{fmt.thb(bp.total)}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{bp.lines.length} line{bp.lines.length === 1 ? '' : 's'}</div>
                  </div>
                </div>
                <div className="bar-track" style={{ height: 6, marginBottom: 10 }}>
                  <div style={{ display: 'flex', height: '100%' }}>
                    <div title={'Received ' + fmt.thb(bp.received)} style={{ width: (bp.received / bp.total * 100) + '%', background: '#4a8a55' }} />
                    <div title={'Awaiting ' + fmt.thb(bp.awaiting)} style={{ width: (bp.awaiting / bp.total * 100) + '%', background: '#C58A24' }} />
                    <div title={'Pending ' + fmt.thb(bp.pending)} style={{ width: (bp.pending / bp.total * 100) + '%', background: 'var(--text-3)' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {bp.lines.map((l: any) => (
                    <div key={l.comp.id + l.idx} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 100px 170px 110px 100px', gap: 8, alignItems: 'center', fontSize: 11.5, padding: '6px 8px', borderRadius: 4, background: l.split.received ? 'rgba(46,106,60,0.05)' : 'var(--surface-alt)' }}>
                      <span style={{ fontWeight: 500 }}>{l.comp.label || l.task?.name}</span>
                      <span className="mono tabular" style={{ textAlign: 'right', color: 'var(--text-2)' }}>{l.split.pct}%</span>
                      <span className="mono tabular" style={{ textAlign: 'right', color: 'var(--text-2)' }}>of {fmt.thb(l.comp.base)}</span>
                      <SplitStateChip state={l.state} />
                      <span className="mono tabular" style={{ textAlign: 'right', fontWeight: 700 }}>{fmt.thb(l.share)}</span>
                      <button
                        className={'btn xs ' + (l.split.received ? '' : 'primary')}
                        disabled={l.state === 'pending-task' || !canReceiveSelf}
                        title={!canReceiveSelf ? 'You do not have permission to confirm receipt' : undefined}
                        onClick={async () => {
                          const was = l.split.received;
                          try { await toggleReceived(l.comp.id, currentUser.id); toast(was ? 'Unticked' : 'Marked as received'); }
                          catch { toast('Could not update this line'); }
                        }}
                        style={{ justifyContent: 'center' }}
                      >
                        {l.split.received ? <><Ico.check /> {l.split.receivedOn}</> : 'Mark received'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card">
            <div className="card-h"><h3>Outstanding actions</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lines.filter((l) => l.state === 'awaiting-payment').length === 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--text-2)', textAlign: 'center', padding: 16 }}>You're all caught up.</div>
              )}
              {lines.filter((l) => l.state === 'awaiting-payment').map((l: any) => (
                <div key={l.comp.id + l.idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, background: 'var(--surface-alt)', borderRadius: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#C58A24' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{l.project.code} · {l.comp.label || l.task?.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>Task complete — tick once you have the funds</div>
                  </div>
                  <div className="mono tabular" style={{ fontWeight: 600 }}>{fmt.thb(l.share)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>How this works</h3></div>
            <div className="card-b" style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
              Each task you contribute to has a base compensation and a % split set by the project admin. Your share = base × your %. Payments are made directly to you, not through the unit — so once you have the money, tick <strong>"Mark received"</strong> on each line. The unit admin uses this to spot any payments that have slipped.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PaymentsPage() {
  const D = useData();
  const toast = useToast();
  const canRemind = useCan('comp.payments.view') === 'allow';
  const [filter, setFilter] = useState('outstanding');
  const [filterUser, setFilterUser] = useState('all');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const sendAllReminders = async () => {
    if (!window.confirm('Send an in-app reminder to everyone with awaiting payments?')) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.compensation.remind();
      toast(`Reminded ${r.reminded} people`);
    } catch { toast('Could not send reminders'); }
    finally { setBulkBusy(false); }
  };
  const remindOne = async (bp: any) => {
    setBusyUserId(bp.user.id);
    try {
      await apiClient.compensation.remind([bp.user.id]);
      toast(`Reminder sent to ${bp.user.name}`);
    } catch { toast('Could not send the reminder'); }
    finally { setBusyUserId(null); }
  };

  const lines: any[] = [];
  D.compensations.forEach((c) => {
    c.splits.forEach((sp, idx) => {
      const t = D.timeline.find((x) => x.id === c.taskId);
      const p = D.projects.find((x) => x.id === c.projectId);
      const u = D.team.find((x) => x.id === sp.user);
      lines.push({ comp: c, split: sp, task: t, project: p, user: u, share: effectiveEarning(c.base, sp), state: splitState(c, sp, D), idx });
    });
  });

  const filtered = lines.filter((l) => {
    if (filter === 'outstanding' && l.state !== 'awaiting-payment') return false;
    if (filterUser !== 'all' && l.user.id !== filterUser) return false;
    return true;
  });

  const byPerson: Record<string, any> = {};
  lines.forEach((l) => {
    if (!byPerson[l.user.id]) byPerson[l.user.id] = { user: l.user, total: 0, received: 0, awaiting: 0, pending: 0, awaitingLines: 0 };
    const bp = byPerson[l.user.id];
    bp.total += l.share;
    if (l.state === 'received') bp.received += l.share;
    else if (l.state === 'awaiting-payment') { bp.awaiting += l.share; bp.awaitingLines += 1; }
    else bp.pending += l.share;
  });

  const totals = {
    total: lines.reduce((s, l) => s + l.share, 0),
    received: lines.filter((l) => l.state === 'received').reduce((s, l) => s + l.share, 0),
    awaiting: lines.filter((l) => l.state === 'awaiting-payment').reduce((s, l) => s + l.share, 0),
    pending: lines.filter((l) => l.state === 'pending-task').reduce((s, l) => s + l.share, 0),
  };

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Payments — Tracker</h2>
          <div className="sub">Payments are made directly to each person. This page lets the admin spot anyone who hasn't ticked "received" yet.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" disabled={filtered.length === 0} onClick={() => {
            downloadCsv(filtered, {
              Person: (l: any) => l.user.name,
              Role: (l: any) => l.user.role,
              Project: (l: any) => l.project.code,
              'Task / milestone': (l: any) => l.comp.label || l.task?.name || '',
              'Split %': (l: any) => l.split.pct,
              'Share (THB)': (l: any) => Math.round(l.share),
              State: (l: any) => SPLIT_STATE[l.state]?.label ?? l.state,
              'Task status': (l: any) => l.task?.status ?? '',
              'Received on': (l: any) => l.split.receivedOn || '',
            }, `payments-${filter}-${dateStamp()}.csv`);
            toast(`Exported ${filtered.length} line${filtered.length === 1 ? '' : 's'}`);
          }}><Ico.dl /> Export</button>
          {canRemind && (
            <button className="btn navy" disabled={bulkBusy} onClick={() => void sendAllReminders()}><Ico.bell /> {bulkBusy ? 'Sending…' : 'Send reminders'}</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        <div className="stat"><div className="label">Total committed</div><div className="v">{fmt.thb(totals.total)}</div><div className="d">{lines.length} split lines</div></div>
        <div className="stat"><div className="label">Received</div><div className="v" style={{ color: '#2E6A3C' }}>{fmt.thb(totals.received)}</div><div className="d">{lines.filter((l) => l.state === 'received').length} lines</div></div>
        <div className="stat" style={{ background: totals.awaiting > 0 ? 'var(--accent-soft)' : undefined, borderColor: totals.awaiting > 0 ? 'var(--accent)' : undefined }}>
          <div className="label">Awaiting receipt confirmation</div>
          <div className="v" style={{ color: 'var(--accent)' }}>{fmt.thb(totals.awaiting)}</div>
          <div className="d">{lines.filter((l) => l.state === 'awaiting-payment').length} lines · needs follow-up</div>
        </div>
        <div className="stat"><div className="label">Pending task done</div><div className="v" style={{ color: 'var(--text-2)' }}>{fmt.thb(totals.pending)}</div><div className="d">tasks still in progress</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="card-h">
            <h3>{filter === 'outstanding' ? 'Outstanding receipts to follow up' : 'All compensation lines'}</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <div className="seg">
                <button className={filter === 'outstanding' ? 'on' : ''} onClick={() => setFilter('outstanding')}>Outstanding</button>
                <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>All lines</button>
              </div>
              <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} style={{ width: 'auto', height: 26, fontSize: 11.5 }}>
                <option value="all">All people</option>
                {D.team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div className="card-b flush">
            {filtered.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 12.5 }}>
                {filter === 'outstanding' ? '✓ No outstanding receipts. Everyone is up to date.' : 'No lines.'}
              </div>
            ) : (
              <table>
                <thead><tr><th>Person</th><th>Project</th><th>Task / milestone</th><th className="num">%</th><th className="num">Share (฿)</th><th>State</th><th>Task status</th></tr></thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <tr key={i}>
                      <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Avatar user={l.user} size="sm" /><span style={{ fontWeight: 500 }}>{l.user.name}</span></span></td>
                      <td><span className="mono" style={{ fontWeight: 600 }}>{l.project.code}</span></td>
                      <td>{l.comp.label || l.task?.name}</td>
                      <td className="num tabular mono">{l.split.pct}%</td>
                      <td className="num tabular mono" style={{ fontWeight: 600 }}>{fmt.thb(l.share)}</td>
                      <td><SplitStateChip state={l.state} /></td>
                      <td>{l.task ? <StatusBadge status={l.task.status} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Per-person summary</h3></div>
          <div className="card-b flush">
            <table>
              <thead><tr><th>Person</th><th className="num">Total</th><th className="num">Received</th><th className="num">Outstanding</th><th></th></tr></thead>
              <tbody>
                {Object.values(byPerson).sort((a: any, b: any) => b.awaiting - a.awaiting).map((bp: any) => (
                  <tr key={bp.user.id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Avatar user={bp.user} size="sm" />
                        <div>
                          <div style={{ fontSize: 12.5, fontWeight: 500 }}>{bp.user.name}</div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{bp.user.role}</div>
                        </div>
                      </span>
                    </td>
                    <td className="num tabular mono">{fmt.thb(bp.total)}</td>
                    <td className="num tabular mono" style={{ color: '#2E6A3C' }}>{fmt.thb(bp.received)}</td>
                    <td className="num tabular mono" style={{ color: bp.awaiting > 0 ? 'var(--accent)' : 'var(--text-2)', fontWeight: bp.awaiting > 0 ? 700 : 400 }}>
                      {bp.awaiting > 0 ? fmt.thb(bp.awaiting) : '—'}
                      {bp.awaiting > 0 && <div style={{ fontSize: 10, color: 'var(--text-2)', fontWeight: 400 }}>{bp.awaitingLines} line{bp.awaitingLines === 1 ? '' : 's'}</div>}
                    </td>
                    <td>
                      {canRemind && bp.awaiting > 0
                        ? <button className="btn ghost icon sm" title="Send reminder" disabled={busyUserId === bp.user.id} onClick={() => void remindOne(bp)}><Ico.bell /></button>
                        : bp.awaiting === 0 ? <span style={{ color: '#2E6A3C', fontSize: 11 }}>✓</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
